import type { DashboardAntwort } from '@symphenia/shared-types';
import { api } from '@/lib/axios';
import type { ExportRequest } from '@/lib/export-request';
import type {
  TimeEntry,
  DailyRecord,
  TenantTimeConfig,
  ActivityType,
  CustomFieldOption,
  StandardFieldDefinition,
  SelectionDataRecord,
  StandardSelectionItem,
  ClockInPayload,
  ClockOutPayload,
  CreateTimeEntryPayload,
  UpdateTimeEntryPayload,
  ListTimeEntriesParams,
  CurrentActivityResponse,
  ReviewTimeEntryPayload,
  UpsertDailyRecordPayload,
  ListDailyRecordsParams,
  UpdateTimeConfigPayload,
  PaginatedResponse,
  TimeSummary,
  DailyWorkTimeRow,
  UserWithTimeSettings,
  UserTimeSettings,
  UpsertUserTimeSettingsPayload,
  TimeProjectRefLite,
  TimeProjectPositionRefLite,
  TimeProjectStepRefLite,
  TimeActivityRefLite,
  TimeUserRefLite,
} from '../types/time-tracking.types';

export const timeEntriesApi = {
  // Clock mode
  clockIn: (payload: ClockInPayload) =>
    api.post<TimeEntry>('/time-entries/clock-in', payload),

  clockOut: (_id: string, payload: ClockOutPayload) =>
    api.post<TimeEntry>('/time-entries/clock-out', payload),

  getActive: () =>
    api.get<TimeEntry | null>('/time-entries/active'),

  /** „Aktuelle Tätigkeiten": laufende Stempelbuchungen aller Mitarbeiter (MANAGER+) */
  getCurrent: () =>
    api.get<CurrentActivityResponse>('/time-entries/current'),

  // CRUD
  list: (params?: ListTimeEntriesParams) =>
    api.get<PaginatedResponse<TimeEntry>>('/time-entries', { params }),

  get: (id: string) =>
    api.get<TimeEntry>(`/time-entries/${id}`),

  create: (payload: CreateTimeEntryPayload) =>
    api.post<TimeEntry>('/time-entries', payload),

  update: (id: string, payload: UpdateTimeEntryPayload) =>
    api.patch<TimeEntry>(`/time-entries/${id}`, payload),

  delete: (id: string) =>
    api.delete<{ message: string }>(`/time-entries/${id}`),

  // Review (Manager+)
  review: (id: string, payload: ReviewTimeEntryPayload) =>
    api.post<TimeEntry>(`/time-entries/${id}/review`, payload),

  // Approve – 2. Stufe nach Prüfung (Manager+)
  approve: (id: string, payload?: ReviewTimeEntryPayload) =>
    api.post<TimeEntry>(`/time-entries/${id}/approve`, payload ?? {}),

  // Prüfung/Genehmigung zurücknehmen (Manager+)
  unreview: (id: string) =>
    api.post<TimeEntry>(`/time-entries/${id}/unreview`, {}),

  // Clear Suspicion – Phase 9 (Manager+)
  clearSuspicion: (id: string) =>
    api.patch<TimeEntry>(`/time-entries/${id}/clear-suspicion`),

  // Summary/Export
  summary: (params: {
    dateFrom: string;
    dateTo: string;
    userId?: string;
    projectId?: string;
  }) =>
    api.get<TimeSummary>('/time-entries/summary', { params }),

  dailyWorkTimes: (params: {
    dateFrom?: string;
    dateTo?: string;
    userId?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
  }) =>
    api.get<PaginatedResponse<DailyWorkTimeRow>>('/time-entries/daily-work-times', { params }),

  /** AP-10 Nachtrag 5: Anfrage statt Aufruf — Download und Mailversand im `ExportButton`. */
  exportRequest: (params: {
    // Darstellungsoptionen
    format?: 'xlsx' | 'docx' | 'pdf';
    /** AP-06-B Regel 3 — leer lassen heisst: Mandanten-Standard. */
    orientation?: 'auto' | 'portrait' | 'landscape';
    columns?: { key: string; label: string }[];
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    filterInfo?: string;
    // Alle Filterparameter (entsprechen ListTimeEntriesDto / ExportTimeEntriesDto)
    dateFrom?: string;
    dateTo?: string;
    userId?: string;
    userIds?: string;
    userIdNot?: string;
    projectId?: string;
    projectIds?: string;
    projectIdNot?: string;
    activityTypeId?: string;
    activityTypeIds?: string;
    activityTypeIdNot?: string;
    status?: string;
    statusNot?: string;
    statusIn?: string;
    noteContains?: string;
    noteNotContains?: string;
    customFieldFilters?: string;
    standardFieldFilters?: string;
    lvFieldFilters?: string;
    hasSuspicion?: boolean;
  }): ExportRequest => {
    const { columns, ...rest } = params;
    return {
      url: '/time-entries/export',
      params: {
        ...rest,
        locale: navigator.language,
        // Spalten als JSON-String übergeben (NestJS DTO erwartet @IsString)
        ...(columns ? { columns: JSON.stringify(columns) } : {}),
      },
    };
  },

  /** AP-10 Nachtrag 5: Anfrage statt Aufruf — Download und Mailversand im `ExportButton`. */
  dailyWorkTimesExportRequest: (params: {
    dateFrom?: string;
    dateTo?: string;
    userId?: string;
    format?: 'xlsx' | 'docx' | 'pdf';
    /** AP-06-B Regel 3 — leer lassen heisst: Mandanten-Standard. */
    orientation?: 'auto' | 'portrait' | 'landscape';
    columns?: string;       // JSON-kodierte Spaltenliste aus der Frontend-Konfiguration
    sortBy?: string;        // aktive Sortierspalte
    sortDir?: 'asc' | 'desc';
    filterInfo?: string;    // lesbare Filterkriterien für die Infozeile im Export
  }): ExportRequest => ({
    url: '/time-entries/daily-work-times-export',
    params: { ...params, locale: navigator.language },
  }),
};

export const dailyRecordsApi = {
  list: (params?: ListDailyRecordsParams) =>
    api.get<PaginatedResponse<DailyRecord>>('/daily-records', { params }),

  get: (date: string) =>
    api.get<DailyRecord>(`/daily-records/${date}`),

  // userId (optional): MANAGER+ pflegen Tagesdaten eines anderen Mitarbeiters.
  // Wird als Query-Param übergeben (nicht im Body — forbidNonWhitelisted).
  upsert: (payload: UpsertDailyRecordPayload, userId?: string) =>
    // date is in the URL – do NOT include it in the body (forbidNonWhitelisted: true would reject it)
    // AP-24: Jedes Feld nur senden, wenn es gesetzt ist — sonst loescht ein
    // Speichern aus dem Diaeten-Formular die haendischen Stunden mit.
    api.put<DailyRecord>(`/daily-records/${payload.date}`, {
      ...(payload.customFields !== undefined && { customFields: payload.customFields }),
      ...(payload.note !== undefined && { note: payload.note }),
      ...(payload.workMinutes !== undefined && { workMinutes: payload.workMinutes }),
      ...(payload.breakMinutes !== undefined && { breakMinutes: payload.breakMinutes }),
      // AP-26: dieselbe Regel, zehn Felder weiter.
      ...(payload.startTime !== undefined && { startTime: payload.startTime }),
      ...(payload.endTime !== undefined && { endTime: payload.endTime }),
      ...(payload.travelMinutes !== undefined && { travelMinutes: payload.travelMinutes }),
      ...(payload.departure !== undefined && { departure: payload.departure }),
      ...(payload.returnTime !== undefined && { returnTime: payload.returnTime }),
      ...(payload.km !== undefined && { km: payload.km }),
      ...(payload.isDriver !== undefined && { isDriver: payload.isDriver }),
      ...(payload.overnight !== undefined && { overnight: payload.overnight }),
      ...(payload.appointment !== undefined && { appointment: payload.appointment }),
      ...(payload.appointmentMinutes !== undefined && { appointmentMinutes: payload.appointmentMinutes }),
    }, { params: userId ? { userId } : undefined }),

  getByDate: (date: string, userId?: string) =>
    api.get<DailyRecord>(`/daily-records/${date}`, { params: userId ? { userId } : undefined }),

  submit: (date: string, userId?: string) =>
    api.post<DailyRecord>(`/daily-records/${date}/submit`, undefined, { params: userId ? { userId } : undefined }),
};

export const timeConfigApi = {
  get: () =>
    api.get<TenantTimeConfig>('/time-config'),

  // Sichere Teilmenge der Config, für alle Buchungsrollen (TIME_BOOKING/EMPLOYEE/MANAGER+)
  // lesbar. Enthält u.a. editPolicy, projectSource/activityTypeSource und beide
  // Custom-Field-Schemata — genau das, was die Auswertungs-Liste (SummaryPage) braucht,
  // ohne den ADMIN-only-Endpoint GET /time-config zu erfordern.
  getClockSettings: () =>
    api.get<TenantTimeConfig>('/time-config/clock-settings'),

  update: (payload: UpdateTimeConfigPayload) =>
    api.patch<TenantTimeConfig>('/time-config', payload),

  getProjects: () =>
    api.get<{ id: string; name: string; projectNumber: string; externalId: string | null }[]>('/time-config/projects'),

  getActivityTypes: () =>
    api.get<ActivityType[]>('/time-config/activity-types'),

  // Type B: daily-record payroll fields (DailyRecordDialog)
  getCustomFieldsSchema: () =>
    api.get<import('../types/time-tracking.types').CustomFieldSchema[] | null>('/time-config/custom-fields'),

  // Type A: time-entry fields (ClockPage / manual mode)
  getTimeEntryCustomFieldsSchema: () =>
    api.get<import('../types/time-tracking.types').CustomFieldSchema[] | null>('/time-config/time-entry-fields'),

  // Standard fields: definitions merged with current tenant config
  getStandardFields: () =>
    api.get<StandardFieldDefinition[]>('/time-config/standard-fields'),

  updateStandardFieldsConfig: (fields: { name: string; visible: boolean; required: boolean }[]) =>
    api.patch<StandardFieldDefinition[]>('/time-config/standard-fields', { fields }),

  getFieldOptions: (fieldName: string) =>
    api.get<CustomFieldOption[]>(`/time-config/custom-field-options/${fieldName}`),

  syncFieldOptions: (
    fieldName: string,
    options: { value: string; externalId?: string }[]
  ) =>
    api.put<CustomFieldOption[]>(
      `/time-config/custom-field-options/${fieldName}`,
      { options }
    ),

  // ── Selection Data: Projects CRUD ──────────────────────────────────────

  getAllProjects: () =>
    api.get<{ id: string; name: string; projectNumber: string; externalId: string | null; isActive: boolean; status: string }[]>('/time-config/projects/all'),

  // AP-35 Phase B: Anlegen, Ändern und Löschen von Projekten liegen im Projektmanagement.

  // ── Selection Data: Activity Types CRUD ────────────────────────────────

  getAllActivityTypes: () =>
    api.get<ActivityType[]>('/time-config/activity-types/all'),

  createActivityType: (payload: { name: string; color?: string; externalId?: string; sortOrder?: number }) =>
    api.post<ActivityType>('/time-config/activity-types', payload),

  updateActivityType: (id: string, payload: { name?: string; color?: string; externalId?: string; isActive?: boolean; sortOrder?: number }) =>
    api.patch<ActivityType>(`/time-config/activity-types/${id}`, payload),

  deleteActivityType: (id: string) =>
    api.delete(`/time-config/activity-types/${id}`),

  // ── Selection Data: Custom Field Group Records ─────────────────────────

  getSelectionDataGroups: () =>
    api.get<{ groupName: string; fieldType: string; count: number }[]>('/time-config/selection-data/groups'),

  getSelectionDataRecords: (groupName: string) =>
    api.get<SelectionDataRecord[]>(`/time-config/selection-data/${groupName}`),

  createSelectionDataRecord: (groupName: string, payload: { values: Record<string, unknown>; fieldType?: string; sortOrder?: number }) =>
    api.post<SelectionDataRecord>(`/time-config/selection-data/${groupName}`, payload),

  updateSelectionDataRecord: (id: string, payload: { values?: Record<string, unknown>; isActive?: boolean; sortOrder?: number }) =>
    api.patch<SelectionDataRecord>(`/time-config/selection-data/record/${id}`, payload),

  deleteSelectionDataRecord: (id: string) =>
    api.delete(`/time-config/selection-data/record/${id}`),

  // ── Standard Selection Items (Projektpositionen, Arbeitsschritte, Betriebsstätten) ──

  getStandardSelectionItems: (category: string) =>
    api.get<StandardSelectionItem[]>(`/time-config/standard-items/${category}`),

  createStandardSelectionItem: (
    category: string,
    payload: {
      name: string;
      externalId?: string;
      nummer?: number;
      projectId?: string;
      parentId?: string;
      sortOrder?: number;
    }
  ) =>
    api.post<StandardSelectionItem>(`/time-config/standard-items/${category}`, payload),

  updateStandardSelectionItem: (
    id: string,
    payload: {
      name?: string;
      externalId?: string;
      nummer?: number;
      projectId?: string;
      parentId?: string;
      isActive?: boolean;
      sortOrder?: number;
    }
  ) =>
    api.patch<StandardSelectionItem>(`/time-config/standard-items/record/${id}`, payload),

  deleteStandardSelectionItem: (id: string) =>
    api.delete(`/time-config/standard-items/record/${id}`),

  // ── Hierarchische Abfragen für Buchungsmaske ──────────────────────────────

  /** V1: Projektpositionen für ein bestimmtes Projekt laden */
  getProjektpositionenByProject: (projectId: string) =>
    api.get<StandardSelectionItem[]>(`/time-config/projektpositionen/${projectId}`),

  /** V1: Arbeitsschritte für eine bestimmte Projektposition laden */
  getArbeitsschritteByProjektposition: (parentId: string) =>
    api.get<StandardSelectionItem[]>(`/time-config/arbeitsschritte/${parentId}`),

  /** V2: Arbeitsschritt anhand IDArbeitsschritt (externalId) suchen – gibt vollständige Hierarchie */
  lookupArbeitsschrittByExternalId: (externalId: string) =>
    api.get<import('../types/time-tracking.types').ArbeitsschrittLookupResult | null>(
      `/time-config/arbeitsschritte/lookup/${encodeURIComponent(externalId)}`
    ),
};

export const userTimeSettingsApi = {
  /**
   * Eigene ZE-Einstellungen — für jeden Angemeldeten.
   *
   * AP-30-F: **Ohne `showPriceFields`.** Ob jemand Preisfelder sieht, sagt das
   * Feldrecht `time.price.view` in `user.permissions`; eine Einstellung daneben
   * wäre die zweite Wahrheit, die dieses Paket beseitigt hat.
   */
  getMe: () => api.get<UserTimeSettings | null>('/time-config/user-settings/me'),

  /** Liste aller Benutzer mit ihren Einstellungen – nur ADMIN+ */
  list: () =>
    api.get<UserWithTimeSettings[]>('/time-config/user-settings'),

  upsert: (userId: string, payload: UpsertUserTimeSettingsPayload) =>
    api.put<UserTimeSettings>(`/time-config/user-settings/${userId}`, payload),
};

// ─── AP-09-E: Picklists für die Buchungsmaske ──────────────────────────────
// Schmale Dropdown-Endpoints (keine Paginierung, keine Admin-Felder). Alle Routen
// liegen unter /time-tracking/picklists/* und sind ab Rolle TIME_BOOKING zugänglich.
export const picklistsApi = {
  projects: (params?: { search?: string }) =>
    api.get<{ data: TimeProjectRefLite[] }>('/time-tracking/picklists/projects', { params }),

  positions: (params: { projectRefId: string; search?: string }) =>
    api.get<{ data: TimeProjectPositionRefLite[] }>('/time-tracking/picklists/positions', { params }),

  steps: (params: { positionRefId: string; search?: string }) =>
    api.get<{ data: TimeProjectStepRefLite[] }>('/time-tracking/picklists/steps', { params }),

  activities: (params?: { search?: string }) =>
    api.get<{ data: TimeActivityRefLite[] }>('/time-tracking/picklists/activities', { params }),

  /** Vollständiger Auswahlbaum (Projekte + Positionen + Schritte + Tätigkeiten) für Filter-Dropdowns */
  bundle: () =>
    api.get<{ data: {
      projects: TimeProjectRefLite[];
      positions: TimeProjectPositionRefLite[];
      steps: TimeProjectStepRefLite[];
      activities: TimeActivityRefLite[];
    } }>('/time-tracking/picklists/bundle'),

  users: (params?: { search?: string }) =>
    api.get<{ data: TimeUserRefLite[] }>('/time-tracking/picklists/users', { params }),
};

export const privacyConsentApi = {
  check: () =>
    api.get<{
      hasConsented: boolean;
      consentedAt?: string;
      version?: string | null;
      /** true nur wenn eine Datenschutzerklärung hinterlegt ist */
      policyConfigured?: boolean;
      currentVersion?: string | null;
      /** true = User soll zur Zustimmung aufgefordert werden */
      needsConsent?: boolean;
    }>('/privacy-consent/status'),

  grant: () =>
    api.post<{ message: string }>('/privacy-consent'),
};

/** AP-40 7.4 — alle Kacheln des Zeiterfassungs-Dashboards in einem Aufruf. */
export const fetchTimeDashboard = () =>
  api.get<{ data: DashboardAntwort }>('/time-entries/dashboard').then((r) => r.data.data)
